import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { T, TWO_HOURS_MS, MAX_TIMEOUT } from '../../../../../constants';
import { SideMenuIcons } from '../../../../../enums/side-menu-icons';
import { useStore, useTranslation } from '../../../../../hooks';
import { NotificationPriority, TournamentEntryType } from '../../../../../enums';
import { SidebarTimer } from '../timer';
import { SidebarCard } from '../card';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { dotFormatter } from '../../../../../helpers';

interface SidebarTournamentProps {
  onClose?: () => void;
}

export const SidebarTournament: React.FC<SidebarTournamentProps> = observer(({ onClose }) => {
  const { gameStore, tournamentStore, sidebarMenuStore, sidebarNotificationStore, userActivityStore } = useStore();
  const { launchData: { data: launchData }, currencySymbol } = gameStore;
  const tournamentInfo = launchData?.tournamentInfo;
  const t = useTranslation();

  useEffect(() => {
    if (tournamentStore.endsSoonNotificationScheduled || !tournamentInfo?.endDate) return;
    tournamentStore.endsSoonNotificationScheduled = true;
    scheduleSidebarNotification(tournamentInfo.endDate);
  }, []);

  const handleDetailsClick = useCallback(() => {
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.TournamentSideBarMenuCard
    });
    sidebarMenuStore.markTournamentsAsSeen();
    tournamentStore.openTournamentFrame(tournamentInfo?.id);
    onClose?.();
  }, [onClose, tournamentInfo?.id]);

  const sendSidebarNotification = useCallback(() => {
    sidebarMenuStore.setHasPromotionsNotification(true);
    sidebarNotificationStore.enqueue(t(T.TOURNAMENT_ENDS_SOON), SideMenuIcons.Tournament, NotificationPriority.High);
  }, []);

  const scheduleSidebarNotification = useCallback((endDate?: string) => {
    if (!endDate) return;

    // Calculate the exact timestamp we want the notification to fire
    const targetNotifyTime = new Date(endDate).getTime() - TWO_HOURS_MS;

    const checkAndSchedule = () => {
      const now = Date.now();
      const delay = targetNotifyTime - now;

      if (delay <= 0) {
        // Time has already passed, or it's exactly time to send
        sendSidebarNotification();
      } else if (delay > MAX_TIMEOUT) {
        // The delay is too long for one setTimeout.
        // Wait the maximum allowed time, then wake up and check again.
        setTimeout(checkAndSchedule, MAX_TIMEOUT);
      } else {
        // The delay is within the safe limit. Schedule the exact moment.
        setTimeout(sendSidebarNotification, delay);
      }
    };

    // Start the scheduling cycle
    checkAndSchedule();
  }, [sendSidebarNotification]);

  const tournamentExtraInfo = useMemo(() => {
    if (!tournamentInfo?.isParticipant) return null;

    const totalPlayers = tournamentInfo.playerCount;

    const buyInAmount = tournamentInfo.buyInAmount || 0;
    const score = tournamentInfo.score || 0;

    const hasBuyIn = tournamentInfo.entryType !== TournamentEntryType.FreeRoll && buyInAmount !== null;
    const rank = dotFormatter(tournamentInfo.rank ?? 0);
    const rankValue = totalPlayers ? `${rank}/${dotFormatter(totalPlayers)}` : rank;

    return (
      <div className="sidebar-card-info-list">
        {
          tournamentInfo.isParticipant ? (
            <>
              <div className="sidebar-card-info-row">
                <span className="sidebar-card-info-label">{t(T.RANK)}:</span>
                <span className="sidebar-card-info-value">{rankValue}</span>
              </div>
              <div className="sidebar-card-info-row">
                <span className="sidebar-card-info-label">{t(T.SCORE)}:</span>
                <span className="sidebar-card-info-value">{dotFormatter(score)}</span>
              </div>
            </>
          ) : null
        }
        {hasBuyIn && (
          <div className="sidebar-card-info-row">
            <span className="sidebar-card-info-label">{t(T.BUY_IN)}:</span>
            <span className="sidebar-card-info-value">{dotFormatter(buyInAmount)} {currencySymbol}</span>
          </div>
        )}
      </div>
    );
  }, [currencySymbol, t, tournamentInfo]);

  if (!tournamentInfo) return null;

  return (
    <SidebarCard
      className="cup"
      title={t(T.TOURNAMENT)}
      currencyCode={currencySymbol}
      onDetailsClick={handleDetailsClick}
      amount={tournamentInfo.prizePoolAmount}
      extraInfo={tournamentExtraInfo}
    >
      <SidebarTimer
        startDate={tournamentInfo.startDate}
        endDate={tournamentInfo.endDate}
      />
    </SidebarCard>
  );
});
